import React, { useState, useEffect } from 'react';
import { patientAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Activity, Clock, Stethoscope, AlertTriangle } from 'lucide-react';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-xs">
      <p className="font-medium text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.dataKey === 'flaeche' ? 'Fläche' : 'Schmerz'}: {p.value}{p.dataKey === 'flaeche' ? ' cm²' : '/10'}
        </p>
      ))}
    </div>
  );
};

export default function PatientStatistics({ patientId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await patientAPI.statistics(patientId);
        setStats(res.data);
      } catch { /* no stats */ }
      finally { setLoading(false); }
    };
    load();
  }, [patientId]);

  if (loading) return <div className="h-48 bg-slate-100 rounded-2xl animate-pulse" />;
  if (!stats || stats.wound_stats.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Keine Statistiken verfügbar. Wundberichte erstellen um Statistiken zu sehen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="patient-statistics">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-slate-900">{stats.total_wounds}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Wunden</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-teal-700">{stats.active_wounds}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Aktiv</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-slate-900">{stats.total_reports}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Berichte</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-rose-600">{stats.wound_stats.reduce((s, w) => s + w.infektionen, 0)}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Infektionen</p>
          </CardContent>
        </Card>
      </div>

      {/* Per-Wound Statistics */}
      {stats.wound_stats.map(ws => {
        const HealIcon = ws.healing_pct > 0 ? TrendingUp : ws.healing_pct < 0 ? TrendingDown : Minus;
        const healColor = ws.healing_pct > 0 ? 'text-emerald-600' : ws.healing_pct < 0 ? 'text-rose-600' : 'text-amber-600';
        return (
          <Card key={ws.wound_id} className="border border-slate-200 shadow-sm" data-testid={`wound-stat-${ws.wound_id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-sm flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-teal-700" />
                  {ws.wundart} - {ws.lokalisation}
                </CardTitle>
                <Badge variant="outline" className={`text-[10px] border ${ws.status === 'aktiv' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                  {ws.status === 'aktiv' ? 'Offen' : 'Verheilt'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Key metrics row */}
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-2 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-center gap-1">
                    <HealIcon className={`w-3.5 h-3.5 ${healColor}`} />
                    <span className={`text-sm font-bold ${healColor}`}>{ws.healing_pct}%</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">Heilung</p>
                </div>
                <div className="p-2 bg-slate-50 rounded-lg">
                  <p className="text-sm font-bold text-slate-900">{ws.current_area}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">cm² aktuell</p>
                </div>
                <div className="p-2 bg-slate-50 rounded-lg">
                  <p className="text-sm font-bold text-amber-600">{ws.current_pain}/10</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">NRS aktuell</p>
                </div>
                <div className="p-2 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3 text-slate-400" />
                    <span className="text-sm font-bold text-slate-900">{ws.days_tracked}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">Tage</p>
                </div>
              </div>

              {/* Area reduction bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Fläche: {ws.first_area} cm² → {ws.current_area} cm²</span>
                  <span className={healColor}>{ws.healing_pct > 0 ? '-' : '+'}{Math.abs(ws.healing_pct)}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${ws.healing_pct > 0 ? 'bg-emerald-500' : ws.healing_pct < 0 ? 'bg-rose-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(Math.abs(ws.healing_pct), 100)}%` }} />
                </div>
              </div>

              {/* Trend Chart */}
              {ws.trend_data.length >= 2 && (
                <div className="h-36" data-testid={`trend-chart-${ws.wound_id}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={ws.trend_data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id={`grad-${ws.wound_id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0F766E" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#0F766E" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="datum" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="flaeche" stroke="#0F766E" strokeWidth={2} fill={`url(#grad-${ws.wound_id})`} dot={{ r: 3, fill: '#0F766E', stroke: '#fff', strokeWidth: 1.5 }} />
                      <Line type="monotone" dataKey="schmerz" stroke="#D97706" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2, fill: '#D97706' }} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="flex items-center justify-center gap-4 text-[10px] text-slate-400">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-teal-700 rounded-full" />Fläche</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-amber-600 rounded-full" />Schmerz</span>
                  </div>
                </div>
              )}

              {ws.infektionen > 0 && (
                <div className="flex items-center gap-2 p-2 bg-rose-50 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-rose-600" />
                  <span className="text-xs text-rose-700 font-medium">{ws.infektionen} Bericht(e) mit Infektionszeichen</span>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
